import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { SchoolDetails, Class as BaseClass } from '@/admin/config/listConfig';
import { TeacherReportItem } from '@/school-management/types/response';
import classNames from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import Button from '@/components/school-management/Button/Button';
import DynamicForm from '@/components/school-management/Form/DynamicForm/DynamicForm';
import GenericTable, {
  GenericTableHandle,
} from '@/components/school-management/GenericTable/GenericTable';
import { FormConfig } from '@/school-management/config/formConfig';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import { classService } from '@/school-management/services/class';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/admin/config/routes';
import ClassModalsManager from '@/components/school-management/SchoolDetailsPage/ClassModalsManager';
import DeleteClassModal from '@/components/school-management/SchoolDetailsPage/DeleteClassModal';
import DeleteClassesPopup from '@/components/school-management/BulkAction/DeleteClasses/DeleteClassesPopup';
import { UserData } from '@/school-management/types/user-data';
import ClassReport from '@/components/school-management/StudentDataModal/GenerateReport/ClassReport';
import AppModal from '@/components/AppModal/AppModal';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import ReportErrorModal from '@/components/school-management/StudentDataModal/GenerateReport/ReportErrorModal';
import { authSelector } from '@/store/reducers/auth';
import { errorSelector } from '@/store/reducers/error';
import { useAppSelector } from '@/store';
import ClassCodeModal from '@/components/school-management/ClassCodeModal/ClassCodeModal';
import FullClassModal from '@/components/FullClassModal/FullClassModal';
import { registerService } from '@/admin/services/register';
import DealLimitReachedMessage from '@/components/DealLimitReachedMessage/DealLimitReachedMessage';
import commonUtils from '@/school-management/utils/common';
import { FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import processing, { XlsxData, XlsxTable } from '@/school-management/utils/processing';
import { useGroupsReportClasses } from '@/features/classes-report/useGroupsReportClasses';
import { normalizeGroupReportItemsToTableRows } from '@/features/classes-report/normalizeGroupReportRows';
import { createAdminClassesReportColumns } from '@/features/classes-report/classesReportTableColumns';
import { CLASSES_REPORT_PAGE_SIZE } from '@/features/classes-report/buildGroupsReportQueryParams';
import type { ClassesReportTableRow } from '@/features/classes-report/normalizeGroupReportRows';

const PAGE_SIZE = CLASSES_REPORT_PAGE_SIZE;
const MAX_BULK_SELECTED_CLASSES = 50;

type Student = {
  id: string;
  name: string;
  email: string;
  status: string;
  progress: number;
  score: number | string;
};
type ClassWithStudents = BaseClass & { studentList: Student[] };

interface ClassesProps {
  school: SchoolDetails;
  selectedClassId: string | null;
  setSelectedClassId: React.Dispatch<React.SetStateAction<string | null>>;
  setShowClassesStatistics: React.Dispatch<React.SetStateAction<boolean>>;
  setShowClassesStatsSection: React.Dispatch<React.SetStateAction<boolean>>;
  addingClass: boolean;
  setAddingClass: React.Dispatch<React.SetStateAction<boolean>>;
  editClass: boolean;
  setEditClass: React.Dispatch<React.SetStateAction<boolean>>;
  showStudentInvite: boolean;
  setShowStudentInvite: React.Dispatch<React.SetStateAction<boolean>>;
  teachers: TeacherReportItem[];
  selectedGrade: { label: string; value: string };
  setSelectedGrade: React.Dispatch<React.SetStateAction<{ label: string; value: string }>>;
  gradeOptions: { label: string; value: string }[];
  classFormConfig: FormConfig;
  handleClassFormSubmit: (values: any) => void;
  newClassDraft: Partial<ClassWithStudents> | null;
  notifType: 'success' | 'error' | 'info';
  setNotifType: React.Dispatch<React.SetStateAction<'success' | 'error' | 'info'>>;
  teacherFilterActive: boolean;
  classFilters?: Record<string, FilterValue>;
  hasAnyFilter?: boolean;
  onCreateNewClass?: () => void;
  refreshSchoolDetails?: () => void;
  /** When bulk row selection is active, parent should disable class filters (e.g. PageHeader). */
  onClassesBulkSelectionChange?: (hasSelection: boolean) => void;
  onSchoolStatisticsRefetch?: () => void;
}

const Classes: React.FC<ClassesProps> = ({
  school,
  selectedClassId,
  setSelectedClassId,
  setShowClassesStatistics,
  setShowClassesStatsSection,
  addingClass,
  setAddingClass,
  editClass,
  setEditClass,
  showStudentInvite,
  setShowStudentInvite,
  teachers,
  selectedGrade,
  setSelectedGrade,
  gradeOptions,
  classFormConfig,
  handleClassFormSubmit,
  newClassDraft,
  notifType,
  setNotifType,
  teacherFilterActive,
  classFilters,
  hasAnyFilter,
  onCreateNewClass,
  refreshSchoolDetails,
  onClassesBulkSelectionChange,
  onSchoolStatisticsRefetch,
}) => {
  const { userRole, user } = useAppSelector(authSelector);
  const errorState = useAppSelector(errorSelector);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [copiedClassId, setCopiedClassId] = useState<string | null>(null);
  const { t } = useAdminTranslation();
  const navigate = useNavigate();
  const { schoolId: routeSchoolId } = useParams<{ schoolId: string }>();
  const shouldReloadAfterNotificationRef = useRef<boolean>(false);
  const classesTableRef = useRef<GenericTableHandle<ClassesReportTableRow>>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const schoolId = user?.metadata?.organizationDetails?.organizationId || '';

  const {
    currentPage,
    setCurrentPage,
    sortBy,
    sortDirection,
    onSortChange,
    isLoadingClasses,
    isFirstLoading,
    classesError,
    fetchedClasses,
    paginationInfo,
    refetch: refetchClassesPage,
    buildClassQueryParams,
  } = useGroupsReportClasses({
    organizationId: schoolId,
    classFilters,
    searchQuery,
    translateForGradeMapper: t,
    failedToFetchMessage: t('schoolDetailsPage.classes.failedToFetch') || '',
  });

  const [showCsvStudentsCreateError, setShowCsvStudentsCreateError] = useState<boolean>(false);
  const [addStudentsToNewGroup, setAddStudentsToNewGroup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [classToDelete, setClassToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showClassReport, setShowClassReport] = useState(false);
  const [selectedClassForReport, setSelectedClassForReport] = useState<{
    id: string;
    name: string;
    grade: string;
    totalStudents: number;
  } | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [isErrorDownloading, setIsErrorDownloading] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  const [showStudentUploadModal, setShowStudentUploadModal] = useState(false);
  const [studentsInSelectedClass, setStudentsInSelectedClass] = useState(0);
  const [showFullClassModal, setShowFullClassModal] = useState(false);
  const [showClassCodeModal, setShowClassCodeModal] = useState(false);
  const [classCode, setClassCode] = useState<string>('');
  const [addStudentsToExistingClass, setAddStudentsToExistingClass] = useState(false);
  const [selectedClassDealId, setSelectedClassDealId] = useState<string>('');
  const [selectedClassName, setSelectedClassName] = useState<string>('');
  const [showDealLimitReachedModal, setShowDealLimitReachedModal] = useState<boolean>(false);

  useEffect(() => {
    if (!errorState.show && loadingReport) {
      setLoadingReport(false);
    }
  }, [errorState.show, loadingReport]);

  const handleViewClass = useCallback(
    (classId: string) => {
      setSelectedClassId(classId);
      const sid = routeSchoolId || schoolId;
      if (sid) {
        navigate(ROUTES.VIEW_CLASS(sid, classId));
      }
    },
    [setSelectedClassId, routeSchoolId, schoolId, navigate]
  );

  const handleAddStudent = async (classId: string) => {
    let isAllowedToRegister: boolean = false;
    try {
      isAllowedToRegister = !(await registerService.isRegistrationLimitReached(user))?.data.data;
    } catch (error: any) {
      setShowDealLimitReachedModal(true);
    }
    if (!isAllowedToRegister) {
      setShowDealLimitReachedModal(true);
      return;
    }
    setSelectedClassId(classId);
    setAddStudentsToExistingClass(true);

    const classData = (tableData as any[]).find((cls: any) => cls.id === classId);

    if (classData?.classCode) {
      setShowClassCodeModal(true);
      setClassCode(classData.classCode);
      return;
    }

    if (classData?.students && classData.students >= 50) {
      setShowFullClassModal(true);
      return;
    }

    const groupName = classData?.name?.trim() || '';
    setSelectedClassName(groupName);

    const userDealId = user?.metadata?.organizationDetails?.dealId;
    if (userDealId) {
      setSelectedClassDealId(userDealId);
    } else {
      try {
        const response = await classService.getClassById(schoolId, classId);

        if (response.data?.status === 'success' && response.data?.data?.dealId) {
          setSelectedClassDealId(response.data.data.dealId);
        } else {
          setSelectedClassDealId('');
        }
      } catch (error) {
        console.error('Error fetching class details:', error);
        setSelectedClassDealId('');
      }
    }

    setStudentsInSelectedClass(classData?.students || 0);
    setShowStudentUploadModal(true);
  };

  const handleDownloadReport = (classId: string) => {
    const classData = (tableData as any[]).find((cls: any) => cls.id === classId);
    if (classData) {
      setSelectedClassForReport({
        id: classId,
        name: classData.name,
        grade: String(classData.gradeId || ''),
        totalStudents: classData.students,
      });
      setShowClassReport(true);
      setLoadingReport(true);
    }
  };

  const handleEditClass = (classId: string) => {
    navigate(ROUTES.EDIT_CLASS(schoolId, classId));
  };

  const handleDeleteClass = (classId: string) => {
    setClassToDelete(classId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!classToDelete) return;

    setIsDeleting(true);
    try {
      if (!schoolId) {
        setNotifMessage(t('dynamicForm.notification.generalError') || '');
        setNotifType('error');
        setNotifOpen(true);
        setIsDeleting(false);
        setShowDeleteModal(false);
        setClassToDelete(null);
        return;
      }
      const response = await classService.deleteClass(schoolId, classToDelete);
      // Wait 2 seconds for DynamoDB stream to update postgres
      await new Promise(resolve => setTimeout(resolve, 2000));

      const isSuccess =
        response.status >= 200 &&
        response.status < 300 &&
        (response.data?.status === 'success' ||
          response.data?.data?.isGroupDeleted === true ||
          response.data?.data === true);

      if (isSuccess) {
        setNotifMessage(t('schoolDetailsPage.classes.classDeletedSuccessfully') || '');
        setNotifType('success');
        setNotifOpen(true);
        setShowDeleteModal(false);

        shouldReloadAfterNotificationRef.current = true;

        if (classesTableRef.current) {
          const notDeletedClasses = (classesTableRef.current.getSelectedItems() || []).filter(
            c => c.id !== classToDelete
          );
          classesTableRef.current.setSelectedItems(notDeletedClasses);
        }

        await refetchClassesPage(currentPage);
        refreshSchoolDetails?.();
      } else {
        setNotifMessage(t('dynamicForm.notification.generalError') || '');
        setNotifType('error');
        setNotifOpen(true);
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      setNotifMessage(t('dynamicForm.notification.generalError') || '');
      setNotifType('error');
      setNotifOpen(true);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setClassToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setClassToDelete(null);
  };

  const onLoadReportError = useCallback(() => {
    setIsServerError(true);
    setLoadingReport(false);
  }, []);

  const closeErrorModal = () => {
    setIsServerError(false);
    setIsErrorDownloading(false);
    setLoadingReport(false);
    setShowClassReport(false);
  };

  const handleReportGenerated = () => {
    setShowClassReport(false);
    setLoadingReport(false);
    setSelectedClassForReport(null);
  };

  const derivedClasses = React.useMemo(
    () => normalizeGroupReportItemsToTableRows(fetchedClasses, t),
    [fetchedClasses, t]
  );

  const tableData = derivedClasses;
  const hasActiveSearch = searchQuery.trim().length > 0;

  useEffect(() => {
    setShowClassesStatsSection(
      isFirstLoading ||
        isLoadingClasses ||
        (tableData && tableData.length > 0) ||
        !!hasAnyFilter ||
        hasActiveSearch
    );
  }, [
    setShowClassesStatsSection,
    tableData,
    hasAnyFilter,
    isLoadingClasses,
    isFirstLoading,
    hasActiveSearch,
  ]);

  useEffect(() => {
    if (isFirstLoading) {
      setShowClassesStatistics(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowClassesStatistics(
        isLoadingClasses || (tableData && tableData.length > 0) || !!hasAnyFilter || hasActiveSearch
      );
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [
    setShowClassesStatistics,
    tableData,
    hasAnyFilter,
    isLoadingClasses,
    isFirstLoading,
    hasActiveSearch,
  ]);

  const filteredTableData = tableData;

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isOnCreateClassPage = currentPath.includes('/create-class');

    if (
      teacherFilterActive &&
      !isLoadingClasses &&
      (!tableData || tableData.length === 0) &&
      !addingClass &&
      schoolId &&
      !isOnCreateClassPage
    ) {
      navigate(ROUTES.CREATE_CLASS(schoolId));
    }
  }, [teacherFilterActive, isLoadingClasses, tableData, addingClass, schoolId, navigate]);

  const onCsvStudentsCreate = async (users: UserData[]) => {
    console.log('users', users);
    console.log('Creating CSV students...');
  };

  const onCloseDealLimitReachedModal = useCallback(() => {
    setShowDealLimitReachedModal(false);
  }, []);

  const [showDeleteClassesPopup, setShowDeleteClassesPopup] = useState(false);
  const [bulkDeleteSelectedClasses, setBulkDeleteSelectedClasses] = useState<
    ClassesReportTableRow[]
  >([]);

  const onBulkClassesAction = useCallback((actionKey: string, items: ClassesReportTableRow[]) => {
    if (actionKey === 'delete_classes') {
      setBulkDeleteSelectedClasses(items);
      setShowDeleteClassesPopup(true);
    }
  }, []);

  const closeDeleteClassesPopup = useCallback(() => {
    setShowDeleteClassesPopup(false);
    classesTableRef.current?.setSelectedItems([]);
  }, []);

  const handleBulkDeleteClassesCompleted = useCallback(() => {
    void refetchClassesPage(currentPage);
    refreshSchoolDetails?.();
    onSchoolStatisticsRefetch?.();
  }, [refetchClassesPage, currentPage, refreshSchoolDetails, onSchoolStatisticsRefetch]);

  const dropdownItems = useMemo(
    () => [
      {
        label: t('schoolDetailsPage.classes.actions.viewClass'),
        action: 'view',
        onClick: handleViewClass,
      },
      {
        label: t('schoolDetailsPage.classes.actions.addStudent'),
        action: 'add',
        onClick: handleAddStudent,
      },
      {
        label: t('schoolDetailsPage.classes.actions.downloadReport'),
        action: 'download',
        onClick: handleDownloadReport,
      },
      {
        label: t('schoolDetailsPage.classes.actions.edit'),
        action: 'edit',
        onClick: handleEditClass,
      },
      {
        label: t('schoolDetailsPage.classes.actions.delete'),
        action: 'delete',
        danger: true,
        onClick: handleDeleteClass,
      },
    ],
    [t, handleViewClass, handleAddStudent, handleDownloadReport, handleEditClass, handleDeleteClass]
  );

  const columns = useMemo(
    () =>
      createAdminClassesReportColumns({
        t,
        copiedClassId,
        setCopiedClassId,
        onNameClick: handleViewClass,
        onCopyClassCode: message => {
          setNotifMessage(message);
          setNotifType('success');
          setNotifOpen(true);
          setTimeout(() => {
            setNotifOpen(false);
          }, 2000);
        },
        dropdownItems,
      }),
    [t, copiedClassId, dropdownItems, handleViewClass]
  );

  const tableName = useMemo(() => {
    return t(`schoolDetailsPage.classes.title`) || '';
  }, [t]);

  const onDownload = useCallback(async () => {
    console.log('Clicked to download classes CSV');

    let classesData: any[] = [];

    if (schoolId) {
      try {
        const pageQueryParam = buildClassQueryParams(1);
        pageQueryParam.page = 0;
        let hasNextPage: boolean = true;

        while (hasNextPage) {
          pageQueryParam.page++;
          const response = await classService.getAllClasses(schoolId, pageQueryParam);

          if (response?.data?.items && response.data.items.length > 0) {
            classesData = [
              ...classesData,
              ...normalizeGroupReportItemsToTableRows(response.data.items, t),
            ];
          }

          hasNextPage = !!response?.data?.hasNextPage;
        }
      } catch (er) {}
    }

    const xlsxColumns = columns.filter(c => !!c.xlsxRender);
    const headerRow = xlsxColumns.map(c => c.xlsxLabel || c.label);

    const xlsxData: XlsxData = {
      fileName: tableName,
      tables: [],
    };

    const skillTable: XlsxTable = {
      name: tableName,
      headerRow: [...headerRow],
      data: [],
    };
    classesData.forEach(l => {
      const row: any = {};
      xlsxColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      skillTable.data.push(row);
    });
    xlsxData.tables.push(skillTable);

    processing.createXlsx(xlsxData);
  }, [schoolId, buildClassQueryParams, columns, tableName, t]);

  // const hasClassesInSystem = paginationInfo?.totalCount !== undefined && paginationInfo.totalCount > 0;
  // const isEmpty = !hasClassesInSystem && (!tableData || tableData.length === 0);
  const isEmpty = !filteredTableData || filteredTableData.length === 0;

  return (
    <div className={`${classNames.teachersMode} ${isEmpty ? classNames.emptyStateMode : ''}`}>
      {classesError && (
        <div>{t('schoolDetailsPage.classes.errorLoading', { error: classesError })}</div>
      )}
      <div
        className={`${classNames.classesDetailsCard} ${isEmpty ? classNames.emptyStateCard : ''}`}
      >
        {!isLoadingClasses && addingClass && (
          <div className={classNames.inlineFormWrapper}>
            <DynamicForm
              config={classFormConfig}
              showPopupOnSubmit={false}
              onCancel={() => setAddingClass(false)}
              onSubmit={handleClassFormSubmit}
              isreturn={true}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 32 }}>
              <Button
                secondary
                onClick={() => {
                  setAddingClass(false);
                  setEditClass(false);
                }}
                style={{ minWidth: 120, marginTop: '12px' }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  document
                    .querySelector('form')
                    ?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }}
                style={{ minWidth: 120, marginTop: '12' }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        <GenericTable
          ref={classesTableRef}
          title={tableName}
          showSearch={true}
          showHeaderInEmptyState={
            isFirstLoading || isLoadingClasses || hasAnyFilter || hasActiveSearch
          }
          searchPlaceholder={t('schoolDetailsPage.classes.searchPlaceholder') || 'Search classes'}
          onSearchQueryChange={query => {
            setSearchQuery(query);

            if ((query || '').trim()) {
              onSortChange?.(' ', 'asc');
            } else if (!(sortBy || '').trim()) {
              onSortChange?.('name', 'asc');
            }

            setCurrentPage(1);
          }}
          onDownload={onDownload}
          downloadFileName={tableName}
          serverSideDownload={true}
          hasData={hasAnyFilter || filteredTableData.length > 0}
          emptyStateTitle={
            isLoadingClasses || hasAnyFilter || hasActiveSearch
              ? ' '
              : t('schoolDetailsPage.classes.zeroState.title') || ''
          }
          emptyStateDescription={
            isLoadingClasses
              ? ' '
              : hasActiveSearch
                ? t('genericTable.noResultsForSearchQuery', {
                    searchQuery: searchQuery,
                  }) || ''
                : hasAnyFilter
                  ? t('schoolDetailsPage.classes.zeroState.filterTitle') || ''
                  : t('schoolDetailsPage.classes.zeroState.description') || ''
          }
          emptyStateSubDescription={
            hasAnyFilter && !isLoadingClasses
              ? t('schoolDetailsPage.classes.zeroState.filterDescription') || ''
              : undefined
          }
          emptyStateAction={
            onCreateNewClass &&
            teachers.length > 0 &&
            !(isFirstLoading || isLoadingClasses || hasAnyFilter || hasActiveSearch) ? (
              <Button onClick={onCreateNewClass} className={classNames.zeroStateActionButton}>
                {t('schoolDetailsPage.classes.zeroState.buttonText')}
              </Button>
            ) : undefined
          }
          columns={columns}
          data={filteredTableData}
          rowKey={(cls: any) => cls.id}
          variant='compact'
          className={classNames.classesTable}
          pageSize={PAGE_SIZE}
          showSortArrows={true}
          showLoader={isFirstLoading || isLoadingClasses}
          serverSidePagination={true}
          totalPages={paginationInfo.totalPages}
          totalCount={paginationInfo.totalCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          serverSideSorting={true}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
          isMultiSelect={true}
          maxSelectItems={MAX_BULK_SELECTED_CLASSES}
          titleRenderOnSelected={num => t('bulkActions.classes.classesSelected', { count: num })}
          selectedActions={[
            {
              text: (count: number) => t('bulkActions.classes.deleteClasses', { count }),
              key: 'delete_classes',
            },
          ]}
          onAction={onBulkClassesAction}
          onItemsSelect={(items: ClassesReportTableRow[]) => {
            onClassesBulkSelectionChange?.(items.length > 0);
          }}
        />
      </div>
      <DealLimitReachedMessage
        show={showDealLimitReachedModal}
        onClose={onCloseDealLimitReachedModal}
      />
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => {
          setNotifOpen(false);
          // Reload classes after notification closes if we have a pending reload
          // if (shouldReloadAfterNotificationRef.current) {
          //   shouldReloadAfterNotificationRef.current = false;
          //   dispatch(entitiesActions.invalidateClasses());
          //   dispatch(entitiesActions.invalidateTeachers());
          //   dispatch(entitiesActions.invalidateStudents());
          //   reloadClassesRef.current?.();
          // }
        }}
      />
      <DeleteClassModal
        show={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        activeStudentsCount={
          classToDelete
            ? (tableData as any[])?.find((cls: any) => cls.id === classToDelete)?.activeStudents ||
              0
            : 0
        }
      />

      <DeleteClassesPopup
        show={showDeleteClassesPopup}
        organizationId={schoolId}
        selectedClasses={bulkDeleteSelectedClasses}
        onClose={closeDeleteClassesPopup}
        onCompleted={handleBulkDeleteClassesCompleted}
      />

      {loadingReport && (
        <AppModal show={true} showBackdrop={false} blockBodyScroll={true}>
          <AppLoaderCircle />
        </AppModal>
      )}

      <ReportErrorModal
        show={isErrorDownloading || isServerError}
        onClose={closeErrorModal}
        isStudentReport={false}
      />

      {showClassReport && selectedClassForReport && (
        <ClassReport
          schoolId={schoolId || ''}
          classId={selectedClassForReport.id}
          className={selectedClassForReport.name}
          classGrade={selectedClassForReport.grade}
          totalStudents={selectedClassForReport.totalStudents}
          onLoadReportError={onLoadReportError}
          onReportGenerated={handleReportGenerated}
        />
      )}

      <ClassCodeModal
        open={showClassCodeModal}
        classCode={classCode}
        onClose={() => {
          setShowClassCodeModal(false);
          setAddStudentsToExistingClass(false);
        }}
      />
      <ClassModalsManager
        showUploadModal={showStudentUploadModal}
        setShowUploadModal={setShowStudentUploadModal}
        addUsersToNewEntity={false}
        setAddUsersToNewEntity={setAddStudentsToExistingClass}
        onUsersCreate={async () => {
          setShowStudentUploadModal(false);
          setAddStudentsToExistingClass(false);
          setSelectedClassDealId('');
          setSelectedClassName('');
          setNotifMessage(t('schoolsListPage.studentCreatedSuccessfully') || '');
          setNotifType('success');
          setNotifOpen(true);
        }}
        setShowUsersCreateError={setShowCsvStudentsCreateError}
        groupId={selectedClassId || ''}
        groupName={selectedClassName}
        dealId={selectedClassDealId}
        isTeacher={false}
        usersInClass={studentsInSelectedClass}
        refreshSchoolDetails={refreshSchoolDetails}
        onStayPageStudentAddComplete={() => {
          setAddStudentsToExistingClass(false);
          setSelectedClassDealId('');
          setSelectedClassName('');
          refreshSchoolDetails?.();
          navigate(ROUTES.VIEW_CLASS(schoolId, selectedClassId || ''));
        }}
      />
      <FullClassModal
        show={showFullClassModal}
        onClose={() => {
          setShowFullClassModal(false);
        }}
      />
    </div>
  );
};

export default Classes;
